import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DeleteDialog from 'Components/DeleteDialog'
import FieldErrors from 'Components/FieldErrors'
import type { ErrorFields } from 'Components/FieldErrors/FieldErrors'
import PageTitle from 'Components/PageTitle'
import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import RichTextEditor from 'Components/TextEditor'
import { WithLoader } from 'Components/WithLoader'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { FaultReportUpdateList } from 'Services/SalesManagement/Features/FaultReport/Components/FaultReportUpdateList'
import {
  changeFaultReport,
  createFaultReport,
  deleteFaultReport,
  type FaultReportDetailShape,
  FaultReportType,
  fetchFaultReport,
  fetchFaultReportOptions,
  fetchFaultReportUpdates,
  resetFaultReportUpdates
} from 'Services/SalesManagement/Features/FaultReport/faultReportSlice'
import useSalesManagementBreadcrumbs from 'Services/SalesManagement/Hooks/useSalesManagementBreadcrumbs'
import useTypedSelector, { useAppDispatch } from 'Store'
import { BACKEND_DATE_TIME_FORMAT, SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { dateFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import {
  DialogContentText,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import type Editor from 'ckeditor5-custom-build'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

interface CustomizedLocationState {
  faultReportType: FaultReportType | null
}

export default function FaultReportDetailPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as CustomizedLocationState

  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)

  const initialFaultReportDetails: FaultReportDetailShape = {
    id: null,
    type: locationState.faultReportType ?? FaultReportType.unplanned,
    title_fi: '',
    description_fi: '',
    start_time: null,
    created: null,
    updated: null
  }
  const [faultReportDetails, setFaultReportDetails] =
    useState<FaultReportDetailShape>(initialFaultReportDetails)

  type FaultReportDetailErrors = ErrorFields<
    Omit<FaultReportDetailShape, 'id' | 'created' | 'updated'>
  >

  const fieldErrorsDefaultState: FaultReportDetailErrors = {
    title_fi: [],
    type: [],
    description_fi: [],
    start_time: []
  }

  const [fieldErrors, setFieldErrors] = useState<FaultReportDetailErrors>(fieldErrorsDefaultState)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const [fiEditor, setFiEditor] = useState<Editor>()

  const isNewInstance = Number.isNaN(instanceIdParam)

  const pageTitle = isNewInstance ? t`Create a new fault report` : t`Edit fault report`

  const {
    faultReportOptions,
    optionsLoading,
    loading,
    faultReportUpdateLoading,
    submitLoading,
    faultReportUpdates,
    deleteLoading
  } = useTypedSelector(state => state.faultReport)

  const user = useTypedSelector(state => state.user.user)

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialFaultReportDetails changes on every re-render and should not be used as a hook dependency.
  useEffect(() => {
    dispatch(fetchFaultReportOptions())
    if (isNewInstance) {
      setFaultReportDetails(initialFaultReportDetails)
      dispatch(resetFaultReportUpdates())
    } else {
      dispatch(
        fetchFaultReport({
          id: instanceIdParam
        })
      )
        .then(unwrapResult)
        .then(res => {
          setFaultReportDetails(res)
        })
        .catch(() => {
          SnackbarUtils.error(t`There was an error fetching the fault report.`)
        })
      dispatch(
        fetchFaultReportUpdates({
          faultReportId: instanceIdParam
        })
      )
        .then(unwrapResult)
        .catch(() => {
          SnackbarUtils.error(t`There was an error fetching the fault report updates.`)
        })
    }
  }, [dispatch, isNewInstance, instanceIdParam])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const breadcrumbs = useSalesManagementBreadcrumbs([{ title: pageTitle }])

  const handleSelectChange = (event: SelectChangeEvent, name: keyof FaultReportDetailShape) => {
    const { value } = event.target
    setFaultReportDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value, BACKEND_DATE_TIME_FORMAT).toString()
    }
    setFaultReportDetails(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const onSaveClick = () => {
    saveEditorContent('fi')
    setIsSaveDialogOpen(true)
  }

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isNewInstance) {
      dispatch(createFaultReport(faultReportDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(SALES_MANAGEMENT_ROOT_URL)
          SnackbarUtils.success(t`Fault report successfully added.`)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error creating the fault report.`)
        })
    } else {
      dispatch(changeFaultReport(faultReportDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(SALES_MANAGEMENT_ROOT_URL)
          SnackbarUtils.success(t`Fault report successfully updated.`)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error editing the fault report.`)
        })
    }
  }

  const onDelete = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen)
  }

  const handleDelete = () => {
    dispatch(
      deleteFaultReport({
        id: instanceIdParam
      })
    )
      .then(unwrapResult)
      .then(() => {
        setIsDeleteDialogOpen(false)
        SnackbarUtils.success(t`Fault report successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the fault report.`)
      })
      .finally(() => {
        navigate(SALES_MANAGEMENT_ROOT_URL)
      })
  }

  const saveEditorContent = (lang: string) => {
    const field = `description_${lang}`
    const editor = fiEditor

    if (!editor) {
      return
    }

    const content = editor.getData()

    setFaultReportDetails(prevState => ({ ...prevState, [field]: content }))
  }

  const isLoading = loading === 'pending' || optionsLoading === 'pending'
  const isSubmitLoading = submitLoading === 'pending'
  const isFaultReportUpdateLoading = faultReportUpdateLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={SALES_MANAGEMENT_ROOT_URL} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <PageTitle>{pageTitle}</PageTitle>
            {!isNewInstance && (
              <>
                <DeleteButton onClick={onDelete}>{t`Delete fault report`}</DeleteButton>
                <DeleteDialog
                  open={isDeleteDialogOpen}
                  handleClose={() => setIsDeleteDialogOpen(false)}
                  handleSubmit={handleDelete}
                  loading={deleteLoading === 'pending'}
                  title={t`Delete fault report`}
                >
                  <Typography component="span" color="text.primary">
                    {t`Are you sure you want to delete the fault report?`}
                  </Typography>
                </DeleteDialog>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
      <WithLoader loading={isLoading}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
              <Box>
                <TextField
                  required
                  sx={{ width: '100%', maxWidth: 500 }}
                  label={t`Title`}
                  value={faultReportDetails.title_fi}
                  onChange={e =>
                    setFaultReportDetails(prevState => ({
                      ...prevState,
                      title_fi: e.target.value
                    }))
                  }
                  error={fieldErrors.title_fi?.length > 0}
                />
                <FieldErrors error={fieldErrors.title_fi} identifier="title_fi" />
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 200 }} required>
                  <InputLabel>{t`Type`}</InputLabel>
                  <Select
                    value={faultReportDetails.type}
                    label={t`Type`}
                    autoWidth
                    onChange={e => handleSelectChange(e, 'type')}
                    error={fieldErrors.type?.length > 0}
                  >
                    {faultReportOptions.type.map(option => (
                      <MenuItem key={option[0]} value={option[0]}>
                        {option[1]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t`Is this a current incident or a planned service break?`}</FormHelperText>
                  <FieldErrors error={fieldErrors.type} />
                </FormControl>
              </Box>
              <Box sx={{ maxWidth: 300 }}>
                <CustomDateTimePicker
                  name="start_time"
                  value={
                    faultReportDetails.start_time ? new Date(faultReportDetails.start_time) : null
                  }
                  onChange={handleDateTimePickerChange}
                  label={t`Start time`}
                  isError={fieldErrors.start_time?.length > 0}
                  isRequired={true}
                >
                  <FieldErrors error={fieldErrors?.start_time} identifier="start_time" />
                </CustomDateTimePicker>
              </Box>
              <Box>
                <StyledLabel>{t`Content`}</StyledLabel>
                <Box sx={{ backgroundColor: 'background.paper', p: 2 }}>
                  <RichTextEditor
                    value={faultReportDetails.description_fi ?? ''}
                    setEditor={setFiEditor}
                    placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                    editor={fiEditor}
                    removePlugins={['ImageInsert', 'ImageResize', 'ImageUpload']}
                  />
                  <FieldErrors error={fieldErrors.description_fi} />
                </Box>
              </Box>
              <SaveDialog
                open={isSaveDialogOpen}
                handleClose={() => setIsSaveDialogOpen(false)}
                handleSubmit={handleSubmit}
                loading={isSubmitLoading}
                title={t`Save fault report`}
              >
                <DialogContentText>
                  <Typography component="span" color="text.primary">
                    {t`Are you sure you want to save the fault report?`}
                  </Typography>
                </DialogContentText>
              </SaveDialog>
              <Box>
                <CustomLoadingButton
                  isLoading={isSubmitLoading}
                  onClick={onSaveClick}
                  size="large"
                  iconPosition="end"
                >
                  {t`Save fault report`}
                </CustomLoadingButton>
              </Box>
            </StyledForm>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FaultReportUpdateList
              faultReportId={instanceIdParam}
              faultReportUpdates={faultReportUpdates}
              hasControlRights={isSuperuser(user)}
              isNewInstance={isNewInstance}
              isLoading={isFaultReportUpdateLoading}
            />
          </Grid>
        </Grid>
      </WithLoader>
    </Box>
  )
}
